<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import questionGroupService from '@/services/questionGroup.service'
import QuestionGroupDrawer from '@/views/questionnaire/question-group/drawer/QuestionGroupDrawer.vue'
import { userAuthState } from '@core/auth'
import Sortable from 'sortablejs'
import { VDataTableServer } from 'vuetify/labs/VDataTable'
import { useTenant } from '@/composables/useTenant'



const auth = userAuthState()
const { isTenant } = useTenant()
const searchQuery = ref('')
const isConfirmDialogVisible = ref(false)
const selectedFormType = ref(null)

definePage({
  meta: {
    name: 'questionnaire-group',
    subject: 'feedback.menu',
    action: 'questionnaire_group',
  },  
})

// Data table options
const sortBy = ref()
const orderBy = ref()

const errors = ref({
  name: [],
  code: [],
  status: [],
})

const onError = error => {
  if (error.data) {
    errors.value = error.data.errors
  }
}

// Headers
const baseHeaders = [
  { title: '', value: 'drag', sortable: false },
  {
    title: 'Label',
    key: 'name',
  },
  {
    title: 'Questions',
    key: 'total_questions',
    sortable: false,
  },

  // {
  //   title: 'Form Type',
  //   key: 'form_type_id',
  // },
  {
    title: 'Status',
    key: 'status',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const hasCustomerType = computed(() => {
  const sel = cookieRef('selectedUnit').value
  const unit =  auth.user?.units?.find(u => u.id === sel)
  
  return !!unit?.config_heads?.some(ch => ch?.type === 'feedback_collection' && ch?.key === 'customer_type')
})

const headers = computed(() => {
  const arr = [...baseHeaders]
  if (hasCustomerType.value || isTenant('bmrc')) {
    const actionsIndex = arr.findIndex(h => h.key === 'actions')
    if (actionsIndex !== -1) {
      arr.splice(actionsIndex, 0, {
        title: isTenant('bmrc') ? 'Applicable Customer' : 'Customer Type',
        key: 'applicable_customer_type',
      })
    }
  }

  return arr
})

const { data: formTypes } = await useApi(
  createUrl('masters/form-types', {
    query: {
      select: ['id', 'name'],
    },
  }),
)

// Set default form type to first item once loaded
watch(
  () => formTypes.value,
  newFormTypes => {
    if (newFormTypes?.length && !selectedFormType.value) {
      selectedFormType.value = newFormTypes[0].id
    }
  },
  { immediate: true },
)

const {
  data: questionGroupsData,
  execute: fetchQuestionGroups,
} = await useApi(createUrl('/masters/question-groups', {
  query: {
    showAll: true,
    q: searchQuery,
    sort: sortBy,
    order_by: orderBy,
    search: {
      unit_id: cookieRef('selectedUnit'),
      form_type_id: selectedFormType,
    },
  },
}))

const questionGroups = computed(() => questionGroupsData.value)
const totalQuestionGroups = computed(() => questionGroupsData.value?.length)

const updateQuestionGroup = data => {
  if (data.id){
    questionGroupService.updateQuestionGroup(data.id, data, ()=>
    {
      fetchQuestionGroups()
      isQuestionGroupDrawerVisible.value = false
      resetErrors()
    }, onError)
   
  }
}

const saveQuestionGroup = data => {
  questionGroupService.addQuestionGroup(data, ()=>
  {
    fetchQuestionGroups()
    isQuestionGroupDrawerVisible.value = false
    resetErrors()

  }, onError)
}

const resetErrors = () => {
  errors.value = {
    name: [],
    code: [],
    status: [],
  }
}

const isQuestionGroupDrawerVisible = ref(false)
const questionGroupDrawerData = ref()
const questionGroupDeleteData = ref()
const questionGroupDrawerType = ref('Add')

const showQuestionGroup = (questionGroupData, type) => {
  questionGroupDrawerData.value = questionGroupData
  questionGroupDrawerType.value = type  
  isQuestionGroupDrawerVisible.value = true
}

const deleteQuestionGroup = async (confirm, data) => {
  if(data?.id && confirm ){
    await questionGroupService.deleteQuestionGroup(data.id, () => {
      fetchQuestionGroups()
      isConfirmDialogVisible.value = false
    }, onError)
  }
}

const showConfirmDialog = questionGroupData => {
  questionGroupDeleteData.value = questionGroupData
  isConfirmDialogVisible.value = true
}


const renderKey = ref(0)

const forceRerender = () => {
  renderKey.value++
}

const serverTable = ref(null)
let sortable = null

const setupSortable = async () => {
  await nextTick()

  const tbody = serverTable.value.$el.querySelector('tbody')
  if (!tbody) return
  sortable?.destroy()
  sortable = Sortable.create(tbody, {
    handle: '.drag-handle',
    animation: 150,
    ghostClass: 'sortable-ghost',
    onEnd(evt) {
      const sortedOrders = questionGroups.value
        .map(q => q.order)
        .sort((a, b) => a - b)

      const [moved] = questionGroups.value.splice(evt.oldIndex, 1)

      questionGroups.value.splice(evt.newIndex, 0, moved)

      questionGroups.value.forEach((q, idx) => {
        q.order = sortedOrders[idx]
      })

      const payload = questionGroups.value.map(q => ({
        id: q.id,
        order: q.order,
      }))

      questionGroupService.reOrderQuestionGroup(payload)

      // emit('reorder', localItems.value)
      forceRerender()
    },
  })

}

onMounted(setupSortable)

watch(renderKey, async () => {
  await nextTick()
  setupSortable()
})


watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value){
    questionGroupDeleteData.value = {}
  }
})
</script>

<template>
  <VCard
    class="border"
    variant="flat"
  >
    <VCardText class="d-flex flex-wrap py-4 gap-3">
      <!-- FORM TYPE FILTER -->
      <AppSelect
        v-model="selectedFormType"
        :items="formTypes"
        :item-title="(item) => item.name"
        :item-value="(item) => item.id"
        placeholder="Form Type"
        style="inline-size: 12rem"
      />

      <VSpacer />

      <div class="d-flex justify-end gap-3">
        <div style="inline-size: 12rem">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search"
            density="compact"
          />
        </div>

        <VBtn
          v-if="$can('create', 'master.question_group')"
          prepend-icon="tabler-plus"
          @click="showQuestionGroup({},'Add')"
        >
          Add
        </VBtn>
      </div>
    </VCardText>

    <VDivider />

    <VDataTableServer
      ref="serverTable"
      :key="renderKey"
      :items="questionGroups"
      :items-length="totalQuestionGroups"
      :items-per-page="-1"
      :headers="headers"
      item-key="id"
      class="text-no-wrap"
      hide-default-footer
    >
      <template #item.name="{ item }">
        <VMenu
          open-on-hover
          :close-on-content-click="false"
        >
          <template #activator="{ props }">
            <div
              v-bind="props"
              class="d-flex flex-column py-2"
            >
              <span class="text-body-1 font-weight-medium">
                {{ item.name?.substring(0, 25) + (item.name?.length > 25 ? '...' : '') || 'NA' }}
              </span>
              <span
                v-if="item.title"
                class="text-body-2 text-medium-emphasis"
              >
                {{ item.title?.substring(0, 30) + (item.title?.length > 30 ? '...' : '') }}
              </span>
            </div>
          </template>
          <VCard>
            <VList>
              <VListItem class="mx-0">
                <template #title>
                  <VListItemTitle class="text-h6 text-primary">
                    {{ item.name || 'NA' }}
                  </VListItemTitle>
                </template>
                <template
                  v-if="item.title"
                  #subtitle
                >
                  <span class="text-body-2">{{ item.title }}</span>
                </template>
              </VListItem>
            </VList>
          </VCard>
        </VMenu>
      </template>
      <template #item.drag>
        <VIcon
          class="drag-handle"
          icon="tabler-arrows-maximize"
          size="20"
        />
      </template>
      <template #item.form_type_id="{ item }">
        <span class="text-capitalize font-weight-medium">{{ item.form_type?.name }}</span>
      </template>
      <template #item.total_questions="{ item }">
        <span class="text-capitalize font-weight-medium">{{ item.questions?.length }}</span>
      </template>
      <template #item.status="{ item }">
        <span class="text-capitalize font-weight-medium">{{ item.status?'Active':"Inactive" }}</span>
      </template>
      <template #item.applicable_customer_type="{ item }">
        <span class="text-capitalize font-weight-medium">{{ item.applicable_customer_type ? item.applicable_customer_type : 'Normal' }}</span>
      </template>
      <!-- Actions -->
      <template #item.actions="{ item }">
        <IconBtn 
          v-if="$can('update', 'master.question_group')"
          @click="showQuestionGroup(item,'Edit')"
        >
          <VIcon icon="tabler-edit" />
        </IconBtn>

        <IconBtn 
          v-if="$can('delete', 'master.question_group')"
          @click="showConfirmDialog(item)"
        >
          <VIcon icon="tabler-trash" />
        </IconBtn>
      </template>

      <template #bottom />
    </VDataTableServer>
  </VCard>

  <QuestionGroupDrawer
    v-model:isDrawerOpen="isQuestionGroupDrawerVisible"
    :question-group-data="questionGroupDrawerData"
    :type="questionGroupDrawerType ?? 'Add'"
    :errors="errors"
    @update:type="questionGroupDrawerType = $event"
    @update:question-group-data="updateQuestionGroup"
    @save-question-group="saveQuestionGroup"
  />
  <ConfirmDialog
    v-model:isDialogVisible="isConfirmDialogVisible"
    confirmation-question="Are you sure?"
    confirm-msg="QuestionGroup Deleted."
    confirm-title="Deleted!"
    :data="questionGroupDeleteData"
    @confirm="deleteQuestionGroup"
  />
</template>

